import { CircleCheckIcon, InfoIcon, Loader2Icon, OctagonXIcon, TriangleAlertIcon, XIcon } from "lucide-react";
import { nanoid } from "nanoid";
import { Toaster as Sonner, ToastT, type ToasterProps, toast } from "sonner";

import { NOTIFICATION_DURATION } from "@configs/constants";

import { Typography } from "./Typography";

/** An opinionated toast component for React */
const ToastManager = ({ ...props }: ToasterProps) => (
	<Sonner
		icons={{
			success: <CircleCheckIcon className="size-4" />,
			info: <InfoIcon className="size-4" />,
			warning: <TriangleAlertIcon className="size-4" />,
			error: <OctagonXIcon className="size-4" />,
			loading: <Loader2Icon className="size-4 animate-spin" />,
		}}
		toastOptions={{
			classNames: {
				toast:
					"cn-toast max-w-[calc(100vw-48px)] min-w-[300px] shadow-[0_3px_6px_-4px_rgba(0,0,0,0.48),0_6px_16px_0_rgba(0,0,0,0.32),0_9px_28px_8px_rgba(0,0,0,0.2)]",
				title: "font-normal text-base text-white",
				description: "font-normal text-sm text-white",
				closeButton:
					"absolute top-5 right-5 opacity-75 cursor-pointer outline-none bg-transparent hover:text-white hover:opacity-85",
				icon: "w-6",
			},
			unstyled: true,
		}}
		className="z-toast!"
		{...props}
	/>
);

type TOnToast = {
	id: string;
	position: "top-left" | "top-right" | "bottom-left" | "bottom-right" | "top-center" | "bottom-center";
	message: string;
	description: string;
	duration: number;
	action: {
		label: string;
		onClick: () => void;
	};
};

/** Show a toast message
 * @param id - The id of the toast
 * @param position - The position of the toast
 * @param message - The message of the toast
 * @param description - The description of the toast
 * @param duration - The duration of the toast
 * @param action - The action of the toast
 * @param props - The props of the toast
 */
const onToast = ({
	id,
	position,
	message,
	description,
	duration,
	action,
	...props
}: Partial<TOnToast> & Omit<ToastT, "id">) => {
	toast.custom(
		(t) => (
			<div className="mb-1.5">
				<Typography type="h2">{message}</Typography>
				<button
					onClick={() => toast.dismiss(t)}
					className={`focus-visible:border-ring focus-visible:ring-ring/50 aria-invalid:ring-destructive/20
						dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive
						dark:aria-invalid:border-destructive/50 group/button hover:bg-muted hover:text-foreground
						dark:hover:bg-muted/50 aria-expanded:bg-muted aria-expanded:text-foreground group absolute top-4 right-2
						inline-flex size-7 shrink-0 cursor-pointer items-center justify-center rounded-[min(var(--radius-md),12px)]
						border border-transparent bg-clip-padding text-sm font-medium whitespace-nowrap transition-all outline-none
						select-none focus-visible:ring-[3px] disabled:pointer-events-none disabled:opacity-50
						in-data-[slot=button-group]:rounded-lg aria-invalid:ring-[3px] [&_svg]:pointer-events-none [&_svg]:shrink-0`}
				>
					<XIcon
						size={16}
						className="text-grey-tertiary transition-all group-hover:text-white group-hover:opacity-75"
					/>
					<span className="sr-only">Close</span>
				</button>
			</div>
		),
		{
			id: id || `Toast-${nanoid()}`,
			className: "py-4 px-6 rounded-lg bg-block",
			position: position || "bottom-left",
			description,
			duration: duration || NOTIFICATION_DURATION.LONG,
			action: {
				label: action?.label,
				onClick: action?.onClick || (() => {}),
			},
			unstyled: true,
			closeButton: true,
			...props,
		},
	);
};

ToastManager.displayName = "ToastManager";
onToast.displayName = "onToast";

export { ToastManager, onToast };
